/**
 * PushPrompt.jsx — src/components/PushPrompt.jsx
 *
 * Предложение подключить уведомления. Показывается авторизованному клиенту,
 * если push не работают на этом устройстве. Откладывается по «Позже» и крестику.
 */
import { useContext, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Button, IconButton, Sheet, Stack, Typography } from '@mui/joy';

import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { LayoutContext } from '../context';
import {
    SNOOZE_CLOSE_MS, SNOOZE_LATER_MS,
    isPromptSnoozed, snoozePrompt,
} from '../shared/pushClient';

const SHOW_DELAY_MS = 5000;   // не выпрыгивать сразу при входе

const TEXTS = {
    enable: {
        title: 'Включить уведомления?',
        body: 'Сообщим, когда заявку примут в работу, назначат замерщика и подготовят документы — даже если сайт закрыт.',
        action: 'Включить',
    },
    ios_install: {
        title: 'Уведомления на iPhone',
        body: 'Чтобы получать уведомления, добавьте сайт на экран «Домой»: кнопка «Поделиться» внизу Safari → «На экран Домой». Затем откройте приложение с домашнего экрана.',
        action: 'Понятно',
    },
    denied: {
        title: 'Уведомления заблокированы',
        body: 'Разрешение на уведомления отключено в браузере. Включите его в настройках сайта: значок замка в адресной строке → «Уведомления» → «Разрешить».',
        action: 'Понятно',
    },
};

export default function PushPrompt() {
    const {
        account, pushState, refreshPushState, subscribeUserToPush,
        modalAuth, showOrder, showQuickOrder, chatModal, newsStories,
    } = useContext(LayoutContext);

    const [open, setOpen] = useState(false);
    const [variant, setVariant] = useState('enable');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!account) { setOpen(false); return; }
        if (!pushState?.checked) return;
        if (!pushState.supported) return;
        if (!pushState.needsPrompt) { setOpen(false); return; }
        if (isPromptSnoozed()) return;

        setVariant(pushState.variant || 'enable');
        const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
        return () => clearTimeout(timer);
    }, [account, pushState?.checked, pushState?.needsPrompt, pushState?.variant, pushState?.supported]);

    const handleEnable = async () => {
        setBusy(true);
        const result = await subscribeUserToPush();
        setBusy(false);

        if (result?.type === 'success') {
            await refreshPushState();
            setOpen(false);
            return;
        }
        if (result?.type === 'ios_need_install') { setVariant('ios_install'); return; }
        if (result?.type === 'permission_denied') { setVariant('denied'); return; }

        // Отклонили системный запрос или произошёл сбой — не настаиваем
        snoozePrompt(SNOOZE_LATER_MS);
        setOpen(false);
    };

    const handleLater = () => { snoozePrompt(SNOOZE_LATER_MS); setOpen(false); };
    const handleClose = () => { snoozePrompt(SNOOZE_CLOSE_MS); setOpen(false); };

    // Не спорим с открытыми модалками и сторис — предложение подождёт
    if (modalAuth || showOrder || showQuickOrder || chatModal?.open || newsStories?.open) return null;

    const texts = TEXTS[variant] || TEXTS.enable;

    return (
        <AnimatePresence>
            {open && (
                <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 26 }}
                    sx={{
                        position: 'fixed',
                        zIndex: 10005,
                        left: { xs: 12, sm: 'auto' },
                        right: { xs: 12, sm: 24 },
                        bottom: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 24 },
                        width: { xs: 'auto', sm: 390 },
                    }}
                >
                    <Sheet sx={{
                        borderRadius: '18px',
                        border: '1px solid',
                        borderColor: 'neutral.outlinedBorder',
                        p: 2,
                        boxShadow: '0 18px 48px rgba(15,23,42,0.22)',
                        bgcolor: 'background.surface',
                    }}>
                        <Stack direction="row" spacing={1.4} alignItems="flex-start">
                            <Box sx={{
                                width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {variant === 'ios_install'
                                    ? <IosShareRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />
                                    : <NotificationsActiveRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography level="title-sm" sx={{ fontWeight: 800, mb: 0.4 }}>
                                    {texts.title}
                                </Typography>
                                <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                                    {texts.body}
                                </Typography>

                                <Stack direction="row" spacing={1} sx={{ mt: 1.4 }}>
                                    {variant === 'enable' ? (
                                        <>
                                            <Button size="sm" loading={busy} onClick={handleEnable} sx={{ fontWeight: 700 }}>
                                                {texts.action}
                                            </Button>
                                            <Button size="sm" variant="plain" color="neutral" onClick={handleLater}>
                                                Позже
                                            </Button>
                                        </>
                                    ) : (
                                        <Button size="sm" variant="soft" onClick={handleClose} sx={{ fontWeight: 700 }}>
                                            {texts.action}
                                        </Button>
                                    )}
                                </Stack>
                            </Box>

                            <IconButton size="sm" variant="plain" color="neutral" onClick={handleClose}>
                                <CloseRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Stack>
                    </Sheet>
                </Box>
            )}
        </AnimatePresence>
    );
}